import { useLocation, useNavigate } from 'react-router-dom'
import { Button } from '@/components/Button'
import { Card } from '@/components/Card'
import { Icon } from '@/components/Icon'
import { LinkButton } from '@/components/LinkButton'
import { Page } from '@/components/Page'

/**
 * What an unknown URL gets instead of a silent redirect to the dashboard.
 *
 * What reaches here is an address matching no route at all — `/reprts` for
 * `/reports`, or a bookmark from a build where a screen had a different name.
 * A bad patient id does not: `/patients/<anything>` matches the patient route
 * and gets that screen's own error, which is the right place to handle it.
 *
 * Redirecting these to the dashboard was the worse answer. A doctor who
 * followed a link and landed on the home screen has no way to tell whether the
 * link was wrong or the app just ignored it — it looks like it worked. Naming
 * the address, and printing it, turns a silent dead end into a wrong turn with
 * a visible cause.
 *
 * It renders inside `AppShell`, so the rail and the search field are already on
 * screen; the fastest way out is usually to search, and that field is one
 * keystroke away.
 */
export function NotFoundScreen() {
  const navigate = useNavigate()
  const { pathname } = useLocation()

  // history.length is 1 on a cold open from a pasted link — there is nothing
  // behind this screen to go back to, so the control would lie.
  const canGoBack = window.history.length > 1

  return (
    <Page>
      <Card>
        <div className="flex flex-col items-start gap-4 py-6 sm:py-10">
          <span className="grid size-14 place-items-center rounded-lg bg-subtle text-fg-subtle">
            <Icon name="search" size="2rem" />
          </span>

          <div className="grid gap-2">
            <h1 className="text-2xl leading-tight font-bold tracking-tight">
              This address does not exist
            </h1>
            <p className="max-w-[52ch] text-sm leading-relaxed text-fg-muted">
              No screen in the app answers to it. It is usually a typed address that is a letter
              off, or a bookmark kept from a version where this screen had a different name.
              Nothing has been lost — every record is still reachable from the rail or the search
              field.
            </p>
          </div>

          {/* The address itself: a one-letter typo is invisible in the browser
              bar and obvious here. Wrapped rather than ellipsed, because the
              part that is wrong is as likely to be the tail as the head. */}
          <p className="max-w-full rounded-md bg-subtle px-3 py-2 font-mono text-xs break-all text-fg-subtle">
            {pathname}
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            {canGoBack && (
              <Button variant="secondary" onClick={() => navigate(-1)}>
                <Icon name="chevronLeft" />
                Go back
              </Button>
            )}
            <LinkButton to="/" variant={canGoBack ? 'outline' : 'primary'}>
              Dashboard
            </LinkButton>
            <LinkButton to="/patients" variant="ghost">
              <Icon name="users" />
              Find a patient
            </LinkButton>
          </div>
        </div>
      </Card>
    </Page>
  )
}
